import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { IUser } from '@core/interfaces/user.interface';
import { AuthService } from '@core/services/auth.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ERoles } from '@enums/roles.enum';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { toEventInvitationSource } from '@utilities/event-payload-mapping.util';
import { UuidUtilitiesService } from '@utilities/uuid-utilities.service';
import { filter, firstValueFrom } from 'rxjs';
import { AcceptCreatorInvitationStore } from './accept-creator-invitation.store';

@Component({
  imports: [DotsSpinnerComponent, TranslateModule, IconComponent, MatButtonModule],
  providers: [AcceptCreatorInvitationStore],
  selector: 'app-accept-creator-invitation',
  templateUrl: './accept-creator-invitation.component.html',
})
export class AcceptCreatorInvitationComponent {
  public readonly validInvitationId = computed<boolean>(() =>
    this.invitationId() ? UuidUtilitiesService.isUuidV4(this.invitationId()!) : false,
  );

  public readonly store = inject(AcceptCreatorInvitationStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly eventTrackingService = inject(EventTrackingService);

  private readonly invitationId = signal<string | null>(null);

  constructor() {
    this.invitationId.set(this.route.snapshot.params['invitationId']);

    effect(() => {
      if (this.validInvitationId()) {
        this.store.acceptInvitation({ invitationId: this.invitationId()! });
      }
    });

    effect(() => {
      if (this.store.token$()) {
        this.handleSignIn();
      }
    });
  }

  public onGoToSignInClick(): void {
    this.router.navigate(['/auth/login']);
  }

  private async handleSignIn(): Promise<void> {
    try {
      await this.authService.signOut(false);
      await this.authService.signInWithCustomToken(this.store.token$()!);
      await firstValueFrom(this.authService.currentFirebaseUser$.pipe(filter((u) => !!u)));

      // The accept endpoint created the DB user - wait for its profile so the accepted event can
      // carry the new user's id/userGroupId (both are auto-filled from the current user, which is
      // still anonymous at this point, so an early fire would be dropped by buildEvent()).
      const dbUser = await firstValueFrom(this.authService.currentDbUser$.pipe(filter((user) => !!user)));
      await this.trackInvitationAccepted(dbUser);

      await this.router.navigate(['/create-password']);
    } catch {
      await this.router.navigate(['/auth/login']);
    }
  }

  private async trackInvitationAccepted(dbUser: IUser): Promise<void> {
    const source = dbUser.roles
      .map((role) => toEventInvitationSource(role.id as ERoles))
      .find((invitationType) => !!invitationType);

    this.eventTrackingService.track(EEventType.userInvitationAccepted, {
      creatorId: dbUser.creatorId ?? undefined,
      source,
    });
  }
}
